import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  WorkoutSession, 
  WorkoutSessionBase, 
  WorkoutSessionSearchParams, 
  ActiveWorkoutSession
} from '../../types/workout-session';
import * as workoutSessionService from '../../services/firebase/workout-session.service';
import { RootState } from '../index';

interface WorkoutSessionState {
  sessions: Record<string, WorkoutSession>;
  currentSession: WorkoutSession | null;
  activeSession: ActiveWorkoutSession | null;
  recentSessions: string[];
  isLoading: boolean;
  error: string | null;
}

const initialState: WorkoutSessionState = {
  sessions: {},
  currentSession: null,
  activeSession: null,
  recentSessions: [],
  isLoading: false,
  error: null
};

// Async thunks
export const createWorkoutSession = createAsyncThunk<
  string,
  { userId: string; sessionData: WorkoutSessionBase },
  { rejectValue: string }
>('workoutSession/createWorkoutSession', async ({ userId, sessionData }, { rejectWithValue }) => {
  try {
    return await workoutSessionService.createWorkoutSession(userId, sessionData);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create workout session');
  }
});

export const fetchWorkoutSession = createAsyncThunk<
  WorkoutSession,
  { userId: string; sessionKey: string },
  { rejectValue: string }
>('workoutSession/fetchWorkoutSession', async ({ userId, sessionKey }, { rejectWithValue }) => {
  try {
    const session = await workoutSessionService.getWorkoutSession(userId, sessionKey);
    if (!session) {
      return rejectWithValue('Workout session not found');
    }
    return session;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch workout session');
  }
});

export const updateWorkoutSession = createAsyncThunk<
  void,
  { userId: string; sessionKey: string; updates: Partial<WorkoutSessionBase> },
  { rejectValue: string }
>('workoutSession/updateWorkoutSession', async ({ userId, sessionKey, updates }, { rejectWithValue }) => {
  try {
    await workoutSessionService.updateWorkoutSession(userId, sessionKey, updates);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update workout session');
  }
});

export const deleteWorkoutSession = createAsyncThunk<
  void,
  { userId: string; sessionKey: string },
  { rejectValue: string }
>('workoutSession/deleteWorkoutSession', async ({ userId, sessionKey }, { rejectWithValue }) => {
  try {
    await workoutSessionService.deleteWorkoutSession(userId, sessionKey);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete workout session');
  }
});

export const fetchUserWorkoutSessions = createAsyncThunk<
  WorkoutSession[],
  { userId: string; limit?: number },
  { rejectValue: string }
>('workoutSession/fetchUserWorkoutSessions', async ({ userId, limit }, { rejectWithValue }) => {
  try {
    return await workoutSessionService.getUserWorkoutSessions(userId, limit);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch user workout sessions');
  }
});

export const searchWorkoutSessions = createAsyncThunk<
  WorkoutSession[],
  { userId: string; searchParams: WorkoutSessionSearchParams },
  { rejectValue: string }
>('workoutSession/searchWorkoutSessions', async ({ userId, searchParams }, { rejectWithValue }) => {
  try {
    return await workoutSessionService.searchWorkoutSessions(userId, searchParams);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to search workout sessions');
  }
});

// Slice
const workoutSessionSlice = createSlice({
  name: 'workoutSession',
  initialState,
  reducers: {
    setCurrentSession: (state, action: PayloadAction<WorkoutSession | null>) => {
      state.currentSession = action.payload;
    },
    setActiveSession: (state, action: PayloadAction<ActiveWorkoutSession | null>) => {
      state.activeSession = action.payload;
    },
    updateActiveSession: (state, action: PayloadAction<Partial<ActiveWorkoutSession>>) => {
      if (state.activeSession) {
        state.activeSession = { ...state.activeSession, ...action.payload };
      }
    },
    resetSessions: (state) => {
      state.sessions = {};
      state.currentSession = null;
      state.activeSession = null;
      state.recentSessions = [];
      state.isLoading = false;
      state.error = null;
    },
    updateWorkoutSessionExerciseMetrics: (
      state, 
      action: PayloadAction<{
        setIndex: number;
        exerciseIndex: number;
        metrics: any[];
      }>
    ) => {
      const { setIndex, exerciseIndex, metrics } = action.payload;
      if (state.activeSession) {
        state.activeSession.setExercisesResults[setIndex].exercises[exerciseIndex].metrics = metrics;
      }
    },
    addWorkoutSessionResult: (state, action: PayloadAction<WorkoutSession>) => {
      const session = action.payload;
      state.sessions[session.sessionKey] = session;

      // Add to recent sessions (if not already there)
      if (!state.recentSessions.includes(session.sessionKey)) {
        state.recentSessions = [
          session.sessionKey,
          ...state.recentSessions.slice(0, 9) // Keep only 10 recent sessions
        ];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // createWorkoutSession
      .addCase(createWorkoutSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createWorkoutSession.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createWorkoutSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create workout session';
      })

      // fetchWorkoutSession
      .addCase(fetchWorkoutSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkoutSession.fulfilled, (state, action) => {
        state.isLoading = false;
        const session = action.payload;
        state.sessions[session.sessionKey] = session;
        state.currentSession = session;
      })
      .addCase(fetchWorkoutSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch workout session';
      })

      // updateWorkoutSession
      .addCase(updateWorkoutSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWorkoutSession.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateWorkoutSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update workout session';
      })

      // deleteWorkoutSession
      .addCase(deleteWorkoutSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteWorkoutSession.fulfilled, (state) => {
        state.isLoading = false;
        if (state.currentSession) {
          const sessionKey = state.currentSession.sessionKey;
          delete state.sessions[sessionKey];
          state.currentSession = null;
          state.recentSessions = state.recentSessions.filter(key => key !== sessionKey);
        }
      })
      .addCase(deleteWorkoutSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete workout session';
      })

      // fetchUserWorkoutSessions
      .addCase(fetchUserWorkoutSessions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserWorkoutSessions.fulfilled, (state, action) => {
        state.isLoading = false;
        const sessions = action.payload;
        
        // Update sessions
        sessions.forEach(session => {
          state.sessions[session.sessionKey] = session;
        });
        
        // Update recent sessions
        state.recentSessions = sessions
          .sort((a, b) => b.dateCreated - a.dateCreated)
          .slice(0, 10)
          .map(session => session.sessionKey);
      })
      .addCase(fetchUserWorkoutSessions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch user workout sessions';
      })

      // searchWorkoutSessions
      .addCase(searchWorkoutSessions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchWorkoutSessions.fulfilled, (state, action) => {
        state.isLoading = false;
        const sessions = action.payload;
        
        // Update sessions
        sessions.forEach(session => {
          state.sessions[session.sessionKey] = session;
        });
      })
      .addCase(searchWorkoutSessions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to search workout sessions';
      });
  }
});

// Actions
export const { 
  setCurrentSession, 
  setActiveSession, 
  updateActiveSession, 
  resetSessions,
  updateWorkoutSessionExerciseMetrics,
  addWorkoutSessionResult
} = workoutSessionSlice.actions;

// Selectors
export const selectWorkoutSessions = (state: RootState) => state.workoutSession.sessions;
export const selectCurrentSession = (state: RootState) => state.workoutSession.currentSession;
export const selectActiveSession = (state: RootState) => state.workoutSession.activeSession;
export const selectRecentSessions = (state: RootState) => {
  return state.workoutSession.recentSessions.map(key => state.workoutSession.sessions[key]);
};
export const selectIsLoading = (state: RootState) => state.workoutSession.isLoading;
export const selectError = (state: RootState) => state.workoutSession.error;

export default workoutSessionSlice.reducer;